import React, { useEffect, useState } from "react";
import { useTable, useSortBy, usePagination } from "react-table";
import { PrevBtn, NextBtn, PaginationWrapper } from "../Styles/PageStyles";
import Images from "../../Images";
import tw from "tailwind-styled-components";
import { PaginationLimit } from "../../Config";
import { IoCopySharp } from "react-icons/io5";
import moment from "moment";
import { capitalizeFirstLetter, handleCopy, truncateText } from "../../helpers";

const Table = ({
    apiData,
    payLandlordRentLoading,
}) => {
    const storageKey = `landlords_lease_page`;
    const [data, setData] = useState([
        {
            deviceName: "",
            extractionTime: "",
            processingTime: "",
            drops: "",
            action: "",
        },
    ]);
    const [selectedRow, setSelectedRow] = useState(null);
    const [isMobileView, setIsMobileView] = useState(window.innerWidth < 768);
    const [openActionMenu, setOpenActionMenu] = useState(null);

    // Load saved page index
    const storageValue = sessionStorage.getItem(storageKey);
    const savedPage = storageValue !== null ? Number(storageValue) : 0;
    const [currentPage, setCurrentPage] = useState(savedPage);
    useEffect(() => {
        setCurrentPage(savedPage);
    }, [savedPage]);

    const toggleRow = (index) => {
        setSelectedRow(selectedRow === index ? null : index);
    };


    useEffect(() => {
        if (apiData) {
            const tempData = apiData.map((item) => ({
                name: getName(item),
                rent: (
                    <span className="text-sm font-semibold text-gray-900">
                        ₹{item.rent}
                    </span>
                ),
                rentDate: getRent(item),
                property: getProperty(item),
                security: '₹' + item.security,
            }));
            setData(tempData);
        }
    }, [apiData, openActionMenu, payLandlordRentLoading]);


    const getProperty = (item) => {
        const name = capitalizeFirstLetter(item?.propertyName || 'N/A');
        return (
            <span className="text-gray-600 text-sm block max-w-[160px] break-words line-clamp-2">
                {truncateText(name, 35)}{" "}
            </span>
        );
    };

    const getRent = (item) => {
        const day = item.rentDate
            ? moment(item.rentDate).format("Do")
            : moment(item.startDate).format("Do");

        return (
            <span className="text-xs font-medium text-gray-600 bg-gray-100 px-3 py-1 rounded-full inline-flex">
                Every {day}
            </span>
        );
    };


    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                !event.target.closest("#menuOptions") &&
                !event.target.closest(".action-btn")
            ) {
                setOpenActionMenu(null);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    const getName = (item) => {
        const name = capitalizeFirstLetter(item?.landlordName || "N/A");

        return (
            <UserImageWrapper>
                <UserImage
                    src={item.profilePicture || Images.Avatar}
                    alt={name}
                />

                <div className="flex flex-col min-w-0">
                    <span className="text-sm font-medium text-gray-900 truncate max-w-[180px]">
                        {name}
                    </span>

                    <div className="flex items-center gap-1 text-xs text-gray-400">
                        <span>#{item.landlordMobile}</span>
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                handleCopy(item.landlordMobile);
                            }}
                            className="hover:text-gray-600 transition"
                            title="Copy number"
                        >
                            <IoCopySharp size={12} />
                        </button>
                    </div>
                </div>
            </UserImageWrapper>
        );
    };


    useEffect(() => {
        const handleResize = () => {
            setIsMobileView(window.innerWidth < 768);
        };

        window.addEventListener("resize", handleResize);

        return () => {
            window.removeEventListener("resize", handleResize);
        };
    }, []);

    const columns = React.useMemo(() => {
        if (isMobileView) {
            return [
                {
                    Header: "Name",
                    accessor: "name",
                },
                {
                    Header: "Property",
                    accessor: "property",
                },
            ];
        }
        return [
            {
                Header: "Landlord",
                accessor: "name",
            },
            {
                Header: "Property",
                accessor: "property",
            },
            {
                Header: "Rent",
                accessor: "rent",
            },
            {
                Header: "Rent Date",
                accessor: "rentDate",
            },

        ];
    }, [isMobileView]);

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        prepareRow,
        pageOptions,
        page,
        state: { pageIndex },
        previousPage,
        nextPage,
        canPreviousPage,
        canNextPage,
    } = useTable(
        {
            data,
            columns,
            initialState: { pageIndex: currentPage, pageSize: PaginationLimit },
        },
        useSortBy,
        usePagination
    );

    useEffect(() => {
        //setCurrentPage(pageIndex);
        sessionStorage.setItem(storageKey, pageIndex);
    }, [pageIndex, storageKey]);

    return (
        <>
            <CustomTable {...getTableProps()}>
                <Thead>
                    {headerGroups.map((headerGroup) => (
                        <Tr {...headerGroup.getHeaderGroupProps()}>
                            {headerGroup.headers.map((column) => (
                                <Th {...column.getHeaderProps(column.getSortByToggleProps())}>
                                    {column.render("Header")}
                                    <span>
                                        {column.isSorted ? (
                                            <img
                                                src={Images.Arrow}
                                                alt="down arrow"
                                                className={`${column.isSortedDesc ? "-rotate-90" : "rotate-90"
                                                    } w-1.5 inline-block ml-1.5`}
                                            />
                                        ) : (
                                            ""
                                        )}
                                    </span>
                                </Th>
                            ))}
                        </Tr>
                    ))}
                </Thead>
                <Tbody {...getTableBodyProps()} hasdata={(apiData?.length > 0).toString()}>
                    {page.map((row, rowIndex) => {
                        prepareRow(row);

                        return (
                            <React.Fragment key={rowIndex}>
                                <Tr
                                    {...row.getRowProps({ style: { height: "80px" } })}
                                    hasdata={(apiData?.length > 0).toString()}
                                >
                                    {row.cells.map((cell, cellIndex) => {
                                        return (
                                            <Td
                                                {...cell.getCellProps()}
                                                onClick={() => {
                                                    if (
                                                        cellIndex === 0 ||
                                                        (cellIndex === 1 && window.innerWidth < 768)
                                                    ) {
                                                        toggleRow(rowIndex);
                                                    }
                                                }}
                                            >
                                                {cell.render("Cell")}
                                            </Td>
                                        );
                                    })}
                                </Tr>
                                {selectedRow === rowIndex && window.innerWidth < 768 && (
                                    <Tr>
                                        <Td colSpan={columns.length}>
                                            <ul className="flex flex-col gap-2">
                                                <li>
                                                    <strong>Rent</strong>{" "}
                                                    {data[rowIndex].rent}
                                                </li>
                                                <li>
                                                    <strong>Rent Date</strong>{" "}
                                                    {data[rowIndex].rentDate}
                                                </li>
                                                <li>
                                                    <strong>Agreement Duration</strong>{" "}
                                                    {data[rowIndex].agreementDuration}
                                                </li>
                                                <li>
                                                    <strong>Action</strong>{" "}
                                                    {data[rowIndex].action}
                                                </li>
                                            </ul>
                                        </Td>
                                    </Tr>
                                )}
                            </React.Fragment>
                        );
                    })}
                </Tbody>
            </CustomTable>
            {apiData?.length > 0 && (
                <PaginationWrapper>
                    <div className="px-2">
                        Page{" "}
                        <em>
                            {pageIndex + 1} of {pageOptions.length}
                        </em>
                    </div>

                    <div className="flex gap-1">
                        <PrevBtn onClick={() => previousPage()} disabled={!canPreviousPage}>
                            <img src={Images.Arrow} alt="arrow" className="w-2 opacity-75 " />
                        </PrevBtn>
                        <NextBtn onClick={() => nextPage()} disabled={!canNextPage}>
                            <img
                                src={Images.Arrow}
                                alt="arrow"
                                className="w-2 rotate-180 opacity-75"
                            />
                        </NextBtn>
                    </div>
                </PaginationWrapper>
            )}
        </>
    );
};

const CustomTable = tw.table` w-full text-gray-600`;
const Thead = tw.thead`border-b uppercase text-gray-600 bg-blue-50`;
const Tbody = tw.tbody`${(props) => (props.hasdata === "true" ? "border-b" : "")}`;
const Tr = tw.tr`${(props) =>
    props.hasdata === "true"
        ? "border-b"
        : ""} rounded-md overflow-hidden hover:bg-slate-50`;
const Th = tw.th`text-left text-sm md:p-3 p-0.5 font-normal text-gray-600`;
const Td = tw.td`p-3 text-sm md:p-3 text-left`;
const UserImageWrapper = tw.div` flex items-center gap-2`;
const UserImage = tw.img`w-10 h-10 rounded-full `;

export default Table;